import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { GROUP_COURSE_THRESHOLD, LARGE_GROUP_COURSE_THRESHOLD, getParticipantCount } from '../../lib/groupCourses';

type CourseLike = {
  id: string | number;
  dayOfWeek?: number;
  startTime?: string;
  endTime?: string;
  raumName?: string | null;
  roomName?: string | null;
  teilnehmer?: string | null;
  participants?: Array<{ name?: string | null }> | null;
};

type TrialLike = {
  id: string | number;
  date: string;
  roomName?: string | null;
  raumName?: string | null;
};

interface ResourceMatrixViewProps {
  days: Date[];
  courses: CourseLike[];
  trials: TrialLike[];
}

const ResourceMatrixView: React.FC<ResourceMatrixViewProps> = ({ days, courses, trials }) => {
  const roomNames = useMemo(() => {
    const names = new Set<string>();
    courses.forEach((c) => {
      const room = (c.raumName || c.roomName || '').trim();
      names.add(room || 'Ohne Raum');
    });
    trials.forEach((t) => {
      const room = (t.raumName || t.roomName || '').trim();
      names.add(room || 'Ohne Raum');
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'de'));
  }, [courses, trials]);

  const gridByRoomAndDay = useMemo(() => {
    const map = new Map<string, number>();
    const add = (room: string, dayKey: string) => {
      const key = `${room}::${dayKey}`;
      map.set(key, (map.get(key) || 0) + 1);
    };

    courses.forEach((c) => {
      const room = (c.raumName || c.roomName || '').trim() || 'Ohne Raum';
      const dow = Number(c.dayOfWeek || 0);
      const day = days.find((d) => ((d.getDay() + 6) % 7) + 1 === dow);
      if (!day) return;
      add(room, format(day, 'yyyy-MM-dd'));
    });

    trials.forEach((t) => {
      const room = (t.raumName || t.roomName || '').trim() || 'Ohne Raum';
      add(room, t.date);
    });

    return map;
  }, [courses, trials, days]);

  const groupLoadByDay = useMemo(() => {
    const map = new Map<string, { groups6Plus: number; groups10Plus: number; participantTotal: number }>();
    const ensure = (dayKey: string) => {
      const existing = map.get(dayKey);
      if (existing) return existing;
      const created = { groups6Plus: 0, groups10Plus: 0, participantTotal: 0 };
      map.set(dayKey, created);
      return created;
    };

    courses.forEach((c) => {
      const dow = Number(c.dayOfWeek || 0);
      const day = days.find((d) => ((d.getDay() + 6) % 7) + 1 === dow);
      if (!day) return;
      const dayKey = format(day, 'yyyy-MM-dd');
      const stats = ensure(dayKey);
      const participantCount = getParticipantCount(c);
      if (participantCount >= GROUP_COURSE_THRESHOLD) stats.groups6Plus += 1;
      if (participantCount >= LARGE_GROUP_COURSE_THRESHOLD) stats.groups10Plus += 1;
      stats.participantTotal += participantCount;
    });

    return map;
  }, [courses, days]);

  if (roomNames.length === 0) {
    return (
      <div className="rounded-lg border p-4 text-sm text-muted-foreground">
        Keine Ressourcenbelegung für die aktuelle Auswahl vorhanden.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border">
        <div className="border-b px-3 py-2 text-sm font-medium">Ressourcenansicht (Zeit x Raum)</div>
        <div className="max-w-full overflow-auto">
          <table className="min-w-[760px] w-full text-xs">
            <thead>
              <tr className="bg-muted/30">
                <th className="sticky left-0 z-10 bg-muted/30 border-b border-r px-3 py-2 text-left">Raum</th>
                {days.map((d) => (
                  <th key={format(d, 'yyyy-MM-dd')} className="border-b px-2 py-2 text-center font-medium">
                    {format(d, 'EEE dd.MM.', { locale: de })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roomNames.map((room) => (
                <tr key={room}>
                  <td className="sticky left-0 z-10 bg-background border-r border-b px-3 py-2 font-medium">{room}</td>
                  {days.map((d) => {
                    const dayKey = format(d, 'yyyy-MM-dd');
                    const count = gridByRoomAndDay.get(`${room}::${dayKey}`) || 0;
                    return (
                      <td key={`${room}-${dayKey}`} className="border-b px-2 py-2 text-center">
                        {count > 0 ? (
                          <span className="inline-flex min-w-6 items-center justify-center rounded bg-primary/10 px-2 py-0.5 font-semibold text-primary">
                            {count}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="rounded-lg border">
        <div className="border-b px-3 py-2 text-sm font-medium">Gruppenkurs-Last (Tag)</div>
        <div className="max-w-full overflow-auto">
          <table className="min-w-[760px] w-full text-xs">
            <thead>
              <tr className="bg-muted/30">
                {days.map((d) => (
                  <th key={`groups-${format(d, 'yyyy-MM-dd')}`} className="border-b px-2 py-2 text-center font-medium">
                    {format(d, 'EEE dd.MM.', { locale: de })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {days.map((d) => {
                  const dayKey = format(d, 'yyyy-MM-dd');
                  const stats = groupLoadByDay.get(dayKey) || { groups6Plus: 0, groups10Plus: 0, participantTotal: 0 };
                  return (
                    <td key={`groupload-${dayKey}`} className="border-b px-2 py-3 align-top">
                      <div className="flex flex-col items-center gap-1">
                        <span className="inline-flex items-center justify-center rounded bg-primary/10 px-2 py-0.5 font-semibold text-primary">
                          {'>=6:'} {stats.groups6Plus}
                        </span>
                        <span className="inline-flex items-center justify-center rounded bg-violet-100 px-2 py-0.5 font-semibold text-violet-700">
                          {'>=10:'} {stats.groups10Plus}
                        </span>
                        <span className="text-muted-foreground">Teiln.: {stats.participantTotal}</span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Die Zahlen zeigen die Anzahl belegter Termine je Raum und Tag (Kurse + Probestunden).
      </p>
    </div>
  );
};

export default ResourceMatrixView;

